import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createRouteCatalog, publicTopLevelSegments } from '../logic/appRouteCatalog'
import type { RouteConfigLike, RouteNode } from '../logic/routeCatalog'

const RouteCatalogContext = createContext<RouteNode | null>(null)
const RouteConfigContext = createContext<readonly RouteConfigLike[] | null>(null)

/**
 * Gives the route picker the app's routes. It sits beside the router, handed
 * the router's own route config, so the picker never imports the router (whose
 * layouts would import the picker back).
 */
export function RouteCatalogProvider({
  routes,
  children,
}: {
  routes: readonly RouteConfigLike[]
  children: ReactNode
}) {
  const catalog = useMemo(() => createRouteCatalog(routes), [routes])
  return (
    <RouteConfigContext.Provider value={routes}>
      <RouteCatalogContext.Provider value={catalog}>{children}</RouteCatalogContext.Provider>
    </RouteConfigContext.Provider>
  )
}

/**
 * The catalog with the signed-out pages (Sign In, Reset Password) too, and
 * their first segments, for a link people open signed out (an email's).
 */
// eslint-disable-next-line react-refresh/only-export-components -- the provider's hook lives beside it
export function usePublicRouteCatalog(): { root: RouteNode; publicSegments: string[] } {
  const routes = useContext(RouteConfigContext)
  if (!routes) throw new Error('usePublicRouteCatalog needs a RouteCatalogProvider above it')
  return useMemo(
    () => ({
      root: createRouteCatalog(routes, { includePublic: true }),
      publicSegments: publicTopLevelSegments(routes),
    }),
    [routes]
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- the provider's hook lives beside it
export function useRouteCatalog(): RouteNode {
  const catalog = useContext(RouteCatalogContext)
  if (!catalog) throw new Error('useRouteCatalog needs a RouteCatalogProvider above it')
  return catalog
}

/** The app's route catalog, or null outside a RouteCatalogProvider (a test). */
// eslint-disable-next-line react-refresh/only-export-components -- the provider's hook lives beside it
export function useOptionalRouteCatalog(): RouteNode | null {
  return useContext(RouteCatalogContext)
}

/** The router's own route config the catalog is read from, or null outside a provider. */
// eslint-disable-next-line react-refresh/only-export-components -- the provider's hook lives beside it
export function useOptionalRouteConfig(): readonly RouteConfigLike[] | null {
  return useContext(RouteConfigContext)
}
